import { useCallback, useMemo, useState } from 'react'

import { faSearch } from '@fortawesome/pro-regular-svg-icons'

import { CompanySearchResultFragment } from '../../../types/graphql'
import { useSearchCompaniesQuery } from '../../fetch/companies'
import { Select, VStack } from '../../govwell-ui'
import { SelectOption } from '../../govwell-ui/components/Select/types'
import useDebounce from '../../hooks/use-debounce'
import { DEFAULT_INPUT_DEBOUNCE_MS } from '../../hooks/use-debounce'
import { formatUserName } from '../../utils'
import Text, { TextSize } from '../Typography/Text'

const getOptionFromCompany = (
  company: CompanySearchResultFragment
): SelectOption<CompanySearchResultFragment> => ({
  customDisplay: (c) => (
    <VStack>
      <Text>
        {c.name} | {c.emailAddress}
      </Text>
      {c.createdByUser && (
        <Text size={TextSize.Small} colorToken="colorTextSecondary">
          Created by {formatUserName(c.createdByUser)}
        </Text>
      )}
    </VStack>
  ),
  label: company.name,
  value: company,
})

type Props = {
  caption?: string
  errorMessage?: string
  getIsOptionDisabled?: (option: SelectOption<CompanySearchResultFragment>) => boolean
  isInvalid?: boolean
  isRequired?: boolean
  label?: string
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void
  value: CompanySearchResultFragment | undefined
  onValueChange: (value: CompanySearchResultFragment | undefined) => void
}
const CompanySearch = ({
  caption,
  errorMessage,
  getIsOptionDisabled,
  isInvalid,
  isRequired,
  label,
  onBlur,
  value,
  onValueChange,
}: Props) => {
  const [searchQuery, setSearchQuery] = useState('')
  const debouncedSearchQuery = useDebounce(searchQuery, DEFAULT_INPUT_DEBOUNCE_MS)
  const { companies, isLoading } = useSearchCompaniesQuery(!searchQuery ? '' : debouncedSearchQuery)

  const options: SelectOption<CompanySearchResultFragment>[] = useMemo(() => {
    return [
      ...(value ? [getOptionFromCompany(value)] : []),
      ...companies.filter((c) => c.id !== value?.id).map(getOptionFromCompany),
    ]
  }, [companies, value])

  const selectedOption = useMemo(
    () => options.find((o) => o.value?.id === value?.id),
    [options, value]
  )

  const handleSelectedOptionChange = useCallback(
    (option: SelectOption<CompanySearchResultFragment> | undefined) => {
      onValueChange(option?.value)
    },
    [onValueChange]
  )

  const getOptionKey = useCallback(
    (c: SelectOption<CompanySearchResultFragment>) => `${c.value.id}`,
    []
  )

  return (
    <Select
      {...(label ? { label } : { ariaLabel: 'Search companies' })}
      caption={caption}
      errorMessage={errorMessage}
      getIsOptionDisabled={getIsOptionDisabled}
      getOptionKey={getOptionKey}
      isAsync
      isInvalid={isInvalid}
      isLoading={isLoading}
      isRequired={isRequired}
      onBlur={onBlur}
      onSearchQueryChange={setSearchQuery}
      onSelectedOptionChange={handleSelectedOptionChange}
      options={options}
      prefixIcon={faSearch}
      selectedOption={selectedOption}
    />
  )
}

export default React.memo(CompanySearch)
